import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { ChartProps } from './types';
import { getSeriesColor, getSeriesPattern } from './color-palette';
import { ChartPatternDefs } from './ChartPatternDefs';
import { ChartTooltip, TooltipData } from './ChartTooltip';

const WIDTH = 500;
const HEIGHT = 400;
const RADIUS = Math.min(WIDTH, HEIGHT) / 2 - 40;

/**
 * Pie chart using D3.js for SVG rendering.
 * Slices use both color and patterns for accessibility.
 * Supports keyboard navigation and tooltips.
 */
export function PieChart({ data, dimensionLabel, measureLabel, onSegmentClick }: ChartProps): React.ReactElement {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);

  const handleSliceInteraction = useCallback(
    (dimensionValue: string, measureValue: number, event: React.MouseEvent | React.FocusEvent) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const target = event.currentTarget as HTMLElement;
      const targetRect = target.getBoundingClientRect();
      setTooltip({
        dimensionValue,
        measureValue,
        dimensionLabel,
        measureLabel,
        x: targetRect.left - rect.left + targetRect.width / 2,
        y: targetRect.top - rect.top,
      });
    },
    [dimensionLabel, measureLabel],
  );

  const hideTooltip = useCallback(() => setTooltip(null), []);

  useEffect(() => {
    if (!svgRef.current || data.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('g.chart-content').remove();

    const g = svg
      .append('g')
      .attr('class', 'chart-content')
      .attr('transform', `translate(${WIDTH / 2},${HEIGHT / 2})`);

    const pie = d3
      .pie<{ dimensionValue: string; measureValue: number }>()
      .value((d) => d.measureValue)
      .sort(null);

    const arc = d3.arc<d3.PieArcDatum<{ dimensionValue: string; measureValue: number }>>()
      .innerRadius(0)
      .outerRadius(RADIUS);

    const arcs = pie(data);

    arcs.forEach((arcData) => {
      const color = getSeriesColor(arcData.data.dimensionValue);
      const patternId = getSeriesPattern(arcData.data.dimensionValue);

      // Solid color slice
      g.append('path')
        .attr('d', arc(arcData) ?? '')
        .attr('fill', color)
        .attr('stroke', 'white')
        .attr('stroke-width', 2);

      // Pattern overlay for accessibility
      const patternGroup = g.append('g').attr('style', `color: ${color}`);
      patternGroup
        .append('path')
        .attr('d', arc(arcData) ?? '')
        .attr('fill', `url(#${patternId})`)
        .attr('opacity', 0.3);
    });
  }, [data]);

  if (data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground" role="status">
        No data available
      </div>
    );
  }

  // Compute positions for keyboard-navigable overlays
  const pie = d3
    .pie<{ dimensionValue: string; measureValue: number }>()
    .value((d) => d.measureValue)
    .sort(null);

  const arcs = pie(data);

  return (
    <div ref={containerRef} className="relative" role="img" aria-label={`Pie chart: ${dimensionLabel} by ${measureLabel}`}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="h-auto w-full max-w-[500px]"
        aria-hidden="true"
      >
        <ChartPatternDefs />
      </svg>
      {/* Keyboard-navigable slice overlays positioned at the centroid */}
      <div className="absolute inset-0" aria-label="Chart data points" role="list">
        {arcs.map((arcData) => {
          const arc = d3.arc<d3.PieArcDatum<{ dimensionValue: string; measureValue: number }>>()
            .innerRadius(0)
            .outerRadius(RADIUS);
          const [cx, cy] = arc.centroid(arcData);
          const absX = (WIDTH / 2 + cx) / WIDTH * 100;
          const absY = (HEIGHT / 2 + cy) / HEIGHT * 100;
          const btnSize = 32;
          const btnW = btnSize / WIDTH * 100;
          const btnH = btnSize / HEIGHT * 100;

          return (
            <button
              key={arcData.data.dimensionValue}
              type="button"
              role="listitem"
              className="absolute rounded-full border-0 bg-transparent p-0 outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              style={{
                left: `${absX - btnW / 2}%`,
                top: `${absY - btnH / 2}%`,
                width: `${btnW}%`,
                height: `${btnH}%`,
              }}
              tabIndex={0}
              aria-label={`${dimensionLabel}: ${arcData.data.dimensionValue}, ${measureLabel}: ${arcData.data.measureValue}`}
              onMouseEnter={(e) => handleSliceInteraction(arcData.data.dimensionValue, arcData.data.measureValue, e)}
              onFocus={(e) => handleSliceInteraction(arcData.data.dimensionValue, arcData.data.measureValue, e)}
              onMouseLeave={hideTooltip}
              onBlur={hideTooltip}
              onClick={() => onSegmentClick?.(arcData.data.dimensionValue)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSegmentClick?.(arcData.data.dimensionValue);
                }
              }}
            />
          );
        })}
      </div>
      <ChartTooltip tooltip={tooltip} />
    </div>
  );
}
